/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  type CommonProps,
  type PolymorphicCommonProps,
  type TooltipObjectProps,
} from '../_common/types';
import { ArrowLeftCircleIconOutline } from '../icons/generated/heroIcons/ArrowLeftCircleIconOutline';
import { ArrowRightCircleIconOutline } from '../icons/generated/heroIcons/ArrowRightCircleIconOutline';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import { useTabsScrollOverflow } from './use-tabs-scroll-overflow';

// Reusable scroll button component
const ScrollButton = ({
  direction,
  isVisible,
  onClick,
  onBackground,
}: {
  direction: 'left' | 'right';
  isVisible: boolean;
  onClick: () => void;
  onBackground: React.ComponentProps<typeof TabsComponent>['onBackground'];
}) => {
  const Icon =
    direction === 'left'
      ? ArrowLeftCircleIconOutline
      : ArrowRightCircleIconOutline;
  const positionClass =
    direction === 'left' ? 'ndl-scroll-left-item' : 'ndl-scroll-right-item';

  return (
    <div
      className={classNames('ndl-scroll-item', positionClass, {
        'ndl-scroll-item-hidden': !isVisible,
        'ndl-scroll-item-on-background-default': onBackground === 'default',
        'ndl-scroll-item-on-background-weak': onBackground === 'weak',
      })}
    >
      <button
        type="button"
        tabIndex={-1}
        className="ndl-scroll-icon-wrapper"
        onClick={onClick}
        aria-hidden="true"
      >
        <Icon className="ndl-scroll-icon" />
      </button>
    </div>
  );
};

type TabId = string;
type Size = 'small' | 'large';
type Fill = 'underline' | 'filled';

type BaseTabsProps<T extends TabId> = {
  /** The currently active tabId */
  value: T;
  /** Callback function triggered when a new tab is selected */
  onChange: (e: T) => void;
  // TODO v5: add leadingVisual & trailingVisual props, to allow for children not to have flex (allowing text ellipsis). Also add a tooltip when ellipsing.
  /** The content dislayed in the tabs */
  children: React.ReactNode | string;
  /** The fill type of the tabs */
  fill?: Fill;
  /**  The size of the tabs */
  size?: Size;
  /** What background the Tabs is placed on. This affects the gradient background of the scroll navigation buttons. */
  onBackground?: 'weak' | 'default';
};

type TabsContextType<T extends TabId> = Required<
  Pick<BaseTabsProps<T>, 'fill' | 'size' | 'onChange' | 'value'>
> & {
  registerTab: (tabId: T, ref: React.RefObject<HTMLElement | null>) => void;
  unregisterTab: (tabId: T) => void;
  scrollTabIntoView: (tabId: T) => void;
  firstTabId: T | null;
  isValuePresent: boolean;
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const TabsContext = React.createContext<TabsContextType<any> | null>(null);

const useTabsContext = () => {
  const context = useContext(TabsContext);

  if (context === null) {
    throw new Error('Tab used without context');
  }

  return context;
};

const getTabPanelId = (tabId: TabId) => `tabpanel-${tabId}`;

const getGeneralTabClasses = (size: Size, fill: Fill) => {
  return {
    'ndl-filled-tab': fill === 'filled',
    'ndl-large': size === 'large',
    'ndl-small': size === 'small',
    'ndl-underline-tab': fill === 'underline',
  };
};

const TabsComponent = <U extends TabId, T extends React.ElementType = 'div'>({
  children,
  size = 'large',
  fill = 'underline',
  onChange,
  value,
  onBackground = 'weak',
  className,
  style,
  as,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, BaseTabsProps<U>>) => {
  const tabRefs = useRef<Map<U, React.RefObject<HTMLElement | null>>>(
    new Map(),
  );
  const containerRef = useRef<HTMLElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs([containerRef, ref]);
  const [firstTabId, setFirstTabId] = useState<U | null>(null);
  const [isValuePresent, setIsValuePresent] = useState(false);
  const valueRef = useRef(value);

  // Use the custom hook for scroll overflow behavior
  const { scrollState, scrollToNextItem, checkOverflow } =
    useTabsScrollOverflow(scrollContainerRef);

  useEffect(() => {
    valueRef.current = value;
    setIsValuePresent(tabRefs.current.has(value));
  }, [value]);

  const registerTab = useCallback(
    (tabId: U, tabRef: React.RefObject<HTMLElement | null>) => {
      tabRefs.current.set(tabId, tabRef);
      const first = tabRefs.current.keys().next().value ?? null;
      setFirstTabId((prev) => (prev === first ? prev : first));

      if (tabId === valueRef.current) {
        setIsValuePresent(true);
      }
    },
    [],
  );

  const unregisterTab = useCallback((tabId: U) => {
    tabRefs.current.delete(tabId);
    const first = tabRefs.current.keys().next().value ?? null;
    setFirstTabId((prev) => (prev === first ? prev : first));

    if (tabId === valueRef.current) {
      setIsValuePresent(false);
    }
  }, []);

  // Check overflow when children change
  useEffect(() => {
    checkOverflow();
  }, [checkOverflow, children]);

  const scrollTabIntoView = useCallback((tabId: U) => {
    const tabRef = tabRefs.current.get(tabId);
    const scrollContainer = scrollContainerRef.current;

    if (tabRef?.current && scrollContainer) {
      const tabRect = tabRef.current.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      const targetScrollLeft =
        scrollContainer.scrollLeft +
        (tabRect.left - containerCenter + tabRect.width / 2);

      scrollContainer.scrollTo({
        behavior: 'smooth',
        left: targetScrollLeft,
      });
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      event.preventDefault();

      const tabIds = Array.from(tabRefs.current.keys());
      const focusedElement = document.activeElement;

      // Find the currently focused tab
      let currentIndex = -1;
      for (let i = 0; i < tabIds.length; i++) {
        const tabRef = tabRefs.current.get(tabIds[i]);
        if (tabRef?.current === focusedElement) {
          currentIndex = i;
          break;
        }
      }

      // If no tab is focused, focus the active tab. Or the first tab if the active tab is not present.
      if (currentIndex === -1) {
        const activeTabRef = tabRefs.current.get(value);
        if (activeTabRef?.current) {
          activeTabRef.current.focus();
        } else if (!isValuePresent && firstTabId !== null) {
          const firstTabRef = tabRefs.current.get(firstTabId);
          if (firstTabRef?.current) {
            firstTabRef.current.focus();
          }
        }
        return;
      }

      let nextIndex: number;

      if (event.key === 'ArrowRight') {
        nextIndex = currentIndex === tabIds.length - 1 ? 0 : currentIndex + 1;
      } else {
        nextIndex = currentIndex === 0 ? tabIds.length - 1 : currentIndex - 1;
      }

      const nextTabId = tabIds[nextIndex];
      const nextTabRef = tabRefs.current.get(nextTabId);

      if (nextTabRef?.current) {
        nextTabRef.current.focus();
        scrollTabIntoView(nextTabId);
      }
    },
    [value, scrollTabIntoView, firstTabId, isValuePresent],
  );

  const classes = classNames(
    'ndl-tabs',
    getGeneralTabClasses(size, fill),
    className,
  );

  const Component: React.ElementType = as ?? 'div';

  return (
    <Component
      className={classes}
      style={style}
      role="tablist"
      aria-orientation="horizontal"
      onKeyDown={handleKeyDown}
      ref={mergedRef}
      {...restProps}
      {...htmlAttributes}
    >
      <ScrollButton
        direction="left"
        isVisible={scrollState.isLeftVisible}
        onClick={() => scrollToNextItem('left')}
        onBackground={onBackground}
      />
      <ScrollButton
        direction="right"
        isVisible={scrollState.isRightVisible}
        onClick={() => scrollToNextItem('right')}
        onBackground={onBackground}
      />
      <TabsContext.Provider
        value={{
          fill,
          firstTabId,
          isValuePresent,
          onChange,
          registerTab,
          scrollTabIntoView,
          size,
          unregisterTab,
          value,
        }}
      >
        <div className="ndl-tabs-container" ref={scrollContainerRef}>
          {children}
        </div>
      </TabsContext.Provider>
    </Component>
  );
};

TabsComponent.displayName = 'Tabs';

/**
 * @remarks
 * When the `description` prop is set, hovering over the tab will show a tooltip with the text provided in the `description` prop. `tooltipProps` can be used to customize the tooltip appearance. The `TooltipObjectProps` type is defined as: `{ root?: Partial<React.ComponentProps<typeof Tooltip>>; trigger?: Partial<React.ComponentProps<typeof Tooltip.Trigger>>; content?: Partial<React.ComponentProps<typeof Tooltip.Content>>; }`, see the Tooltip component for more details.
 */
type TabProps<T extends TabId> = {
  /** The id of the tab */
  id: T;
  /** The content of the tab */
  children: React.ReactNode | string;
  /** A string that will be shown as a tooltip when hovering over the button  it also acts as an aria-label- {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label} */
  description?: string;
  /** Props for the tooltip component. */
  tooltipProps?: TooltipObjectProps;
  /** Additional class name */
  className?: string;
  /** Whether the tab is disabled */
  isDisabled?: boolean;
};

const TabsTab = <
  U extends TabId = TabId,
  T extends React.ElementType = 'button',
>({
  children,
  id: tabId,
  isDisabled = false,
  className,
  description,
  tooltipProps,
  style,
  as,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, TabProps<U>>) => {
  const {
    size,
    fill,
    value,
    onChange,
    registerTab,
    unregisterTab,
    scrollTabIntoView,
    firstTabId,
    isValuePresent,
  } = useTabsContext();
  const internalRef = useRef<HTMLElement>(null);

  // Create a merged ref that handles both ref objects and ref functions
  const mergedRef = useMergeRefs([
    internalRef as React.RefObject<HTMLElement>,
    ref as React.RefObject<HTMLElement>,
  ]);

  // Register/unregister tab for keyboard navigation
  useEffect(() => {
    registerTab(tabId, internalRef);

    return () => {
      unregisterTab(tabId);
    };
  }, [tabId, registerTab, unregisterTab]);

  const baseClasses = classNames({
    ...getGeneralTabClasses(size, fill),
    'ndl-disabled': isDisabled,
    'ndl-selected': value === tabId,
  });

  const Component: React.ElementType = as ?? 'button';

  const classes = classNames('ndl-tab', baseClasses, className);

  // Only the active tab should be in the tab sequence initially
  // Other tabs can be focused via arrow keys but are not in tab sequence
  const tabIndex =
    value === tabId || (!isValuePresent && tabId === firstTabId) ? 0 : -1;

  return (
    <Tooltip
      type="simple"
      {...tooltipProps?.root}
      isDisabled={description === undefined}
    >
      <Tooltip.Trigger {...tooltipProps?.trigger} hasButtonWrapper>
        <Component
          className={classes}
          style={style}
          onClick={() => {
            if (!isDisabled) {
              onChange(tabId);
              scrollTabIntoView(tabId);
            }
          }}
          role="tab"
          id={tabId}
          aria-disabled={isDisabled}
          aria-label={description}
          aria-selected={value === tabId}
          aria-controls={getTabPanelId(tabId)}
          tabIndex={tabIndex}
          ref={mergedRef}
          {...restProps}
          {...htmlAttributes}
        >
          <span className="ndl-tab-content">{children}</span>
          {fill === 'underline' && <span className="ndl-tab-underline" />}
        </Component>
      </Tooltip.Trigger>
      <Tooltip.Content {...tooltipProps?.content}>
        {description}
      </Tooltip.Content>
    </Tooltip>
  );
};

TabsTab.displayName = 'Tabs.Tab';

type TabPanelProps = {
  /** The content displayed in the tab panel */
  children: React.ReactNode;
  /** The currently selected tabId. The tab is visible if the value is equal to the tabId */
  value: TabId;
  /** The id of the tab panel */
  tabId: TabId;
  /** Additional class name */
  className?: string;
};

const TabsTabPanel = <T extends React.ElementType = 'div'>({
  as,
  children,
  value,
  tabId,
  style,
  className,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, TabPanelProps>) => {
  const Component: React.ElementType = as ?? 'div';

  return (
    <>
      {value === tabId ? (
        <Component
          role="tabpanel"
          id={getTabPanelId(tabId)}
          aria-labelledby={tabId}
          style={style}
          className={classNames(className)}
          ref={ref}
          {...restProps}
          {...htmlAttributes}
        >
          {children}
        </Component>
      ) : null}
    </>
  );
};

TabsTabPanel.displayName = 'Tabs.TabPanel';

type TabBadgeProps = {
  /** The content of the badge */
  children: React.ReactNode;
};

const TabBadge = ({
  children,
  ref,
  htmlAttributes,
  style,
  className,
  ...restProps
}: CommonProps<'span', TabBadgeProps>) => {
  const classes = classNames('ndl-tab-badge', className);
  return (
    <Typography
      ref={ref}
      variant="subheading-small"
      className={classes}
      style={style}
      htmlAttributes={htmlAttributes}
      {...restProps}
    >
      {children}
    </Typography>
  );
};

const Tabs = Object.assign(TabsComponent, {
  Badge: TabBadge,
  Tab: TabsTab,
  TabPanel: TabsTabPanel,
});

export { Tabs };
